import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers";
import { LoadingState } from "../../components";
import { CompactSummary } from "./components/summary-card";
import { FilterChips } from "./components/filter-chips";
import { Table } from "../../features/survey-management/survey-summary/table";
import { FTPBreadCrumbs } from "../../components-new";
import { useParams } from "react-router";
import { PageShell } from "../../layouts/PageShell";
// Custom TableHeader with improved styling
const TableHeader = ({ title }: { title: string }) => {
  return (
    <div className="flex items-center gap-2 mb-2.5 px-1">
      <div className="h-1 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
      <h3 className="text-xs font-semibold text-gray-800 tracking-tight">
        {title}
      </h3>
    </div>
  );
};
import { DemographicProfileForm } from "./components/demographic-profile-form";
import { DocumentDetailView } from "./components/document-detail-view";
import { DocumentList } from "./components/document-list";
import { ColumnTransformerModal } from "./components/column-transformer-modal";
import { ShadowStatsDrawer } from "./components/shadow-stats-drawer";
import { toJS } from "mobx";
import _ from "lodash";
import { safe_get } from "../../report-visuals/report-utils";
import Masonry from "@mui/lab/Masonry";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Fab, Tooltip } from "@mui/material";
import { Transform as TransformIcon } from "@mui/icons-material";

const GeopolTransformerComponent = observer(() => {
  const rootStore = useStore();
  const { userStore } = rootStore;
  const { projectid } = useParams<{ projectid?: string }>();

  // Check authorization - only root or client allowed
  const isRootOrClient = userStore?.isRoot() || userStore?.isClient();

  // Safety check - ensure rootStore and store exist
  const store = rootStore?.geopolTransformerStore;

  // Call all hooks before any early returns
  useEffect(() => {
    if (store) {
      store.initialize();
    }
  }, [store]);

  // Helper function to create breakdown data
  const createBreakdownData = (
    groupBy: string,
    getValue?: (doc: any) => any,
    documents: any[] = []
  ) => {
    const grouped = _.groupBy(documents, (doc) => {
      if (getValue) {
        return getValue(doc);
      }
      if (groupBy.includes(".")) {
        const value = safe_get(doc, groupBy, null);
        return value !== null && value !== undefined ? value : "N/A";
      }
      if (groupBy === "surveyor") {
        return doc.surveyor_name || doc.surveyor_id || "N/A";
      }
      const value = doc[groupBy];
      return value !== null && value !== undefined ? value : "N/A";
    });

    return Object.entries(grouped)
      .map(([value, docs]) => ({
        value: value || "N/A",
        count: docs.length,
      }))
      .sort((a, b) => b.count - a.count);
  };

  // Date Breakdown
  const dateBreakdownData = useMemo(
    () => createBreakdownData("dt_month_date", undefined, store?.filteredDocuments || []),
    [store?.filteredDocuments]
  );
  const dateColumns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: dateBreakdownData.reduce((sum, item) => sum + item.count, 0),
      },
    ],
    [dateBreakdownData]
  );

  // Surveyor Breakdown
  const surveyorBreakdownData = useMemo(() => {
    const docs = store?.filteredDocuments || [];
    const grouped = _.groupBy(docs, (doc) => {
      return doc.surveyor_name || doc.surveyor_id || "N/A";
    });
    return Object.entries(grouped)
      .map(([value, docs]) => {
        const doc = docs[0];
        return {
          surveyor_id: doc.surveyor_id,
          surveyor_name: doc.surveyor_name || value,
          value: value,
          count: docs.length,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [store?.filteredDocuments]);
  const surveyorColumns = useMemo(
    () => [
      {
        Header: "Surveyor",
        accessor: "surveyor_name",
        Cell: ({ row }: any) => {
          const data = row.original;
          return (
            <div>
              {data.surveyor_id} - {data.surveyor_name}
            </div>
          );
        },
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: surveyorBreakdownData.reduce((sum, item) => sum + item.count, 0),
      },
    ],
    [surveyorBreakdownData]
  );

  // Gender Breakdown
  const genderBreakdownData = useMemo(
    () =>
      createBreakdownData(
        "demographic_profile_of_respondents.gender_gender_select",
        undefined,
        store?.filteredDocuments || []
      ),
    [store?.filteredDocuments]
  );
  const genderColumns = useMemo(
    () => [
      {
        Header: "Gender",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: genderBreakdownData.reduce((sum, item) => sum + item.count, 0),
      },
    ],
    [genderBreakdownData]
  );

  // Age Range Breakdown
  const ageBreakdownData = useMemo(
    () =>
      createBreakdownData(
        "demographic_profile_of_respondents.age_range_age_select",
        undefined,
        store?.filteredDocuments || []
      ),
    [store?.filteredDocuments]
  );
  const ageColumns = useMemo(
    () => [
      {
        Header: "Age Range",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: ageBreakdownData.reduce((sum, item) => sum + item.count, 0),
      },
    ],
    [ageBreakdownData]
  );

  // Professional Background Breakdown
  const backgroundBreakdownData = useMemo(
    () =>
      createBreakdownData(
        "demographic_profile_of_respondents.professional_background_background_select",
        undefined,
        store?.filteredDocuments || []
      ),
    [store?.filteredDocuments]
  );
  const backgroundColumns = useMemo(
    () => [
      {
        Header: "Professional Background",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: backgroundBreakdownData.reduce(
          (sum, item) => sum + item.count,
          0
        ),
      },
    ],
    [backgroundBreakdownData]
  );

  // Professional Experience Breakdown
  const experienceBreakdownData = useMemo(
    () =>
      createBreakdownData(
        "demographic_profile_of_respondents.professional_experience_experience_select",
        undefined,
        store?.filteredDocuments || []
      ),
    [store?.filteredDocuments]
  );
  const experienceColumns = useMemo(
    () => [
      {
        Header: "Professional Experience",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: experienceBreakdownData.reduce(
          (sum, item) => sum + item.count,
          0
        ),
      },
    ],
    [experienceBreakdownData]
  );

  // Metro City Breakdown
  const cityBreakdownData = useMemo(
    () =>
      createBreakdownData(
        "demographic_profile_of_respondents.metro_city_city_select",
        undefined,
        store?.filteredDocuments || []
      ),
    [store?.filteredDocuments]
  );
  const cityColumns = useMemo(
    () => [
      {
        Header: "Metro City",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: cityBreakdownData.reduce((sum, item) => sum + item.count, 0),
      },
    ],
    [cityBreakdownData]
  );

  // Political Preference Breakdown
  const preferenceBreakdownData = useMemo(
    () =>
      createBreakdownData(
        "demographic_profile_of_respondents.political_preference_preference_select",
        undefined,
        store?.filteredDocuments || []
      ),
    [store?.filteredDocuments]
  );
  const preferenceColumns = useMemo(
    () => [
      {
        Header: "Political Preference",
        accessor: "value",
        Footer: "Total",
      },
      {
        Header: "Count",
        accessor: "count",
        Footer: preferenceBreakdownData.reduce(
          (sum, item) => sum + item.count,
          0
        ),
      },
    ],
    [preferenceBreakdownData]
  );


  const handleBreakdownClick = (key: string, value: any, display: string) => {
    if (store) {
      store.applyFilter(key, value, display);
    }
  };

  const handleRemoveFilter = (key: string) => {
    if (store) {
      store.removeFilter(key);
    }
  };

  const handleResetFilters = () => {
    if (store) {
      store.resetFilters();
    }
  };

  const handleDocumentRowClick = (document: any) => {
    if (store && document) {
      // document is already the GeopolDocument object (row.original)
      const documentId = document.id;
      if (documentId) {
        store.setSelectedDocument(documentId);
      }
    }
  };

  const handleBackToList = () => {
    if (store) {
      store.setSelectedDocument(null);
    }
  };

  const handleSaveProfile = async (demographicOverride: any, generatedProfile: any) => {
    if (store && store.selectedDocumentId) {
      await store.saveProfile(store.selectedDocumentId, demographicOverride, generatedProfile);
    }
  };

  // Early returns after all hooks are called
  if (!isRootOrClient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">🔒</div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-3">
            Access Denied
          </h1>
          <p className="text-gray-600 text-lg">
            Only admin and client users can access this feature.
          </p>
          <div className="mt-6 h-1 w-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!rootStore || !store) {
    return <LoadingState />;
  }

  if (store.loading) {
    return <LoadingState />;
  }

  // If a document is selected, show detail/edit view
  if (store.selectedDocumentId) {
    // Check if document is mapped
    const isDocumentMapped = store.selectedDocumentId &&
      store.transformerConfig?.row_transformers?.[store.selectedDocumentId] !== undefined;

    const crumbs = [
      { name: "DashBoard", href: projectid ? `/project/${projectid}` : "/", current: false },
      { name: "Geopol Transformer", href: "#", current: false },
      { name: "Document Details", href: "#", current: true },
    ];

    // Custom title component for detail view
    const detailTitle = (
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackToList}
          className="group flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all duration-200 font-medium text-sm"
          aria-label="Back to document list"
        >
          <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform duration-200" />
          Back
        </button>
        <div className="h-6 w-px bg-gray-300"></div>
        <div className="flex items-center gap-2.5">
          <span className="text-xl font-bold text-gray-900">
            Document Details
          </span>
          {isDocumentMapped && (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded-md">
              <CheckCircleIcon className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-700">Mapped</span>
            </div>
          )}
        </div>
        <div className="text-xs text-gray-500 font-mono bg-gray-100 px-2.5 py-1 rounded-md ml-2">
          {store.selectedDocumentId}
        </div>
      </div>
    );

    return (
      <>
        <PageShell
          title={detailTitle}
          headerActions={<CompactSummary summary={store.summary} onOpenStats={() => store.setShowShadowStatsDrawer(true)} />}
          breadcrumbs={<FTPBreadCrumbs crumbs={crumbs} />}
          contentPadding="px-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left Part: Edit Profile Form */}
            <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-120px)] lg:overflow-y-auto">
              <DemographicProfileForm
                document={store.selectedDocument}
                documentId={store.selectedDocumentId}
                onSave={handleSaveProfile}
                onCancel={handleBackToList}
                rootStore={rootStore}
              />
            </div>

            {/* Right Part: Detail View with Audio and Questions */}
            <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-120px)] lg:overflow-y-auto">
              <DocumentDetailView
                documentId={store.selectedDocumentId}
                documentDetails={store.selectedDocumentDetails}
                loading={store.loadingDetails}
              />
            </div>
          </div>
        </PageShell>

        {/* Shadow Stats Drawer - also available in detail view */}
        <ShadowStatsDrawer store={store} />
      </>
    );
  }

  // Default list view
  const crumbs = [
    { name: "DashBoard", href: projectid ? `/project/${projectid}` : "/", current: false },
    { name: "Geopol Transformer", href: "#", current: true },
  ];

  return (
    <>
      <PageShell
        title="Geopol Transformer"
        headerActions={<CompactSummary summary={store.summary} onOpenStats={() => store.setShowShadowStatsDrawer(true)} />}
        breadcrumbs={<FTPBreadCrumbs crumbs={crumbs} />}
        contentPadding="px-4"
      >
        <div className="w-full space-y-6">
          <FilterChips
            filters={store.filters}
            onRemove={handleRemoveFilter}
            onResetAll={handleResetFilters}
          />

          {/* Breakdown Tables in Masonry Grid */}
          <Masonry columns={{ sm: 1, md: 2, lg: 4, xl: 5 }} spacing={2}>
            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Date" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={dateBreakdownData}
                  columns={dateColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick("dt_month_date", row.value, `Date: ${row.value}`)
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Surveyor" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={surveyorBreakdownData}
                  columns={surveyorColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "surveyor",
                      row.surveyor_name,
                      `Surveyor: ${row.surveyor_name}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Gender" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={genderBreakdownData}
                  columns={genderColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "demographic_profile_of_respondents.gender_gender_select",
                      row.value,
                      `Gender: ${row.value}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Age Range" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={ageBreakdownData}
                  columns={ageColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "demographic_profile_of_respondents.age_range_age_select",
                      row.value,
                      `Age Range: ${row.value}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Background" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={backgroundBreakdownData}
                  columns={backgroundColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "demographic_profile_of_respondents.professional_background_background_select",
                      row.value,
                      `Background: ${row.value}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Experience" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={experienceBreakdownData}
                  columns={experienceColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "demographic_profile_of_respondents.professional_experience_experience_select",
                      row.value,
                      `Experience: ${row.value}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by City" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={cityBreakdownData}
                  columns={cityColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "demographic_profile_of_respondents.metro_city_city_select",
                      row.value,
                      `City: ${row.value}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>

            <div className="flex flex-col w-100 md:w-0 group">
              <TableHeader title="Interviews by Political Preference" />
              <div className="bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 group-hover:border-indigo-200 [&_table_td]:px-2.5 [&_table_td]:py-2 [&_table_th]:px-2.5 [&_table_th]:py-2 [&_table]:text-xs [&_table_tbody_tr]:cursor-pointer [&_table_tbody_tr:hover]:bg-indigo-50/50">
                <Table
                  data={preferenceBreakdownData}
                  columns={preferenceColumns}
                  onRowClick={(row) =>
                    handleBreakdownClick(
                      "demographic_profile_of_respondents.political_preference_preference_select",
                      row.value,
                      `Preference: ${row.value}`
                    )
                  }
                  initialSortBy={[{ id: "count", desc: true }]}
                />
              </div>
            </div>
          </Masonry>

          {/* Document List Table - Full Width */}
          <div className="mt-8">
            <DocumentList
              documents={store.filteredDocuments}
              onRowClick={handleDocumentRowClick}
              rowTransformers={store.transformerConfig?.row_transformers || {}}
            />
          </div>
        </div>
      </PageShell>

      {/* Floating Action Button */}
      <Tooltip title="Edit Column Transformers" placement="left">
        <Fab
          color="primary"
          aria-label="edit column transformers"
          onClick={() => store.setShowColumnTransformerModal(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
            backgroundColor: "#3A80F4",
            "&:hover": {
              backgroundColor: "#2563EB",
              transform: "scale(1.1)",
            },
            transition: "all 0.3s ease",
            boxShadow: "0 4px 20px rgba(58, 128, 244, 0.4)",
          }}
        >
          <TransformIcon />
        </Fab>
      </Tooltip>

      {/* Column Transformer Modal */}
      <ColumnTransformerModal store={store} />

      {/* Shadow Stats Drawer */}
      <ShadowStatsDrawer store={store} />
    </>
  );
});

export default GeopolTransformerComponent;
